import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import Topbar from "@/components/layout/Topbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, TrashSimple, CaretDown, CaretRight, CashRegister, ReceiptX, Scales } from "@phosphor-icons/react";
import NumberInput from "@/components/ui/NumberInput";

const fmtARS = (n) => (Number(n) || 0).toLocaleString("es-AR", { style: "currency", currency: "ARS" });
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function PasivosSaldosPage() {
  const [entidades, setEntidades] = useState([]);
  const [detalles, setDetalles] = useState({}); // entidad_id -> {deudas, pagos}
  const [expanded, setExpanded] = useState({});
  const [cajas, setCajas] = useState([]);
  const [mediosPago, setMediosPago] = useState([]);

  // Nueva deuda dialog
  const [deudaOpen, setDeudaOpen] = useState(false);
  const [deudaEntidad, setDeudaEntidad] = useState(null);
  const [deudaForm, setDeudaForm] = useState({ descripcion: "", monto_total: "", moneda: "ARS", fecha: todayISO(), observaciones: "" });
  const [savingDeuda, setSavingDeuda] = useState(false);

  // Nuevo pago dialog
  const [pagoOpen, setPagoOpen] = useState(false);
  const [pagoDeuda, setPagoDeuda] = useState(null);
  const [pagoForm, setPagoForm] = useState({ monto: "", moneda_pago: "ARS", cotizacion: "", fecha: todayISO(), medio_pago_id: "", caja_id: "", observaciones: "" });
  const [savingPago, setSavingPago] = useState(false);

  // Delete targets
  const [delDeuda, setDelDeuda] = useState(null);
  const [delPago, setDelPago] = useState(null);

  const loadEntidades = useCallback(async () => {
    try {
      const { data } = await api.get("/pasivos/entidades");
      setEntidades(data || []);
    } catch (e) {
      toast.error("No se pudieron cargar las entidades");
    }
  }, []);

  const loadCajas = useCallback(async () => {
    try {
      // Usamos /medios-pago/mios (scope=ambos) que devuelve los medios ya
      // resueltos con la caja imputable para el usuario logueado.
      // Excluimos 'cheques' porque el pago con cheque requiere flujo aparte.
      const [{ data: c }, { data: m }] = await Promise.all([
        api.get("/cajas"),
        api.get("/medios-pago/mios", { params: { scope: "ambos" } }).catch(() => ({ data: [] })),
      ]);
      setCajas(c || []);
      setMediosPago((m || []).filter((x) => x.tipo !== "cheques" && x.caja_id));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadEntidades(); loadCajas(); }, [loadEntidades, loadCajas]);

  const loadDetalle = async (eid) => {
    try {
      const { data } = await api.get(`/pasivos/entidades/${eid}/detalle`);
      setDetalles((d) => ({ ...d, [eid]: data }));
    } catch (e) {
      toast.error("No se pudo cargar el detalle");
    }
  };

  const toggle = async (eid) => {
    const isOpen = !!expanded[eid];
    setExpanded((s) => ({ ...s, [eid]: !isOpen }));
    if (!isOpen && !detalles[eid]) {
      await loadDetalle(eid);
    }
  };

  const openNuevaDeuda = (entidad) => {
    setDeudaEntidad(entidad);
    setDeudaForm({ descripcion: "", monto_total: "", moneda: "ARS", fecha: todayISO(), observaciones: "" });
    setDeudaOpen(true);
  };

  const guardarDeuda = async () => {
    const monto = parseFloat(deudaForm.monto_total);
    if (!deudaForm.descripcion.trim()) return toast.error("Descripción obligatoria");
    if (!Number.isFinite(monto) || monto <= 0) return toast.error("Monto inválido");
    setSavingDeuda(true);
    try {
      await api.post("/pasivos/deudas", {
        entidad_id: deudaEntidad.id,
        descripcion: deudaForm.descripcion,
        monto_total: monto,
        moneda: deudaForm.moneda || "ARS",
        fecha: deudaForm.fecha || todayISO(),
        observaciones: deudaForm.observaciones || null,
      });
      toast.success("Deuda cargada");
      setDeudaOpen(false);
      await loadDetalle(deudaEntidad.id);
      loadEntidades();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingDeuda(false);
    }
  };

  const openNuevoPago = (entidad, deuda) => {
    setPagoDeuda({ ...deuda, _entidad: entidad });
    const monedaDeuda = (deuda.moneda || "ARS").toUpperCase();
    setPagoForm({
      monto: String(deuda.saldo || ""),
      moneda_pago: monedaDeuda,
      cotizacion: "",
      fecha: todayISO(),
      medio_pago_id: "",
      caja_id: "",
      observaciones: "",
    });
    setPagoOpen(true);
  };

  const guardarPago = async () => {
    const monto = parseFloat(pagoForm.monto);
    if (!Number.isFinite(monto) || monto <= 0) return toast.error("Monto inválido");
    if (!pagoForm.medio_pago_id) return toast.error("Elegí un medio de pago");
    const medio = mediosPago.find((m) => m.id === pagoForm.medio_pago_id);
    if (!medio?.caja_id) return toast.error("El medio de pago no tiene caja imputable configurada");
    const monedaPago = (pagoForm.moneda_pago || "ARS").toUpperCase();
    const monedaDeuda = (pagoDeuda?.moneda || "ARS").toUpperCase();
    const cot = parseFloat(pagoForm.cotizacion);
    if ((monedaPago === "USD" || monedaDeuda === "USD") && monedaPago !== monedaDeuda) {
      if (!Number.isFinite(cot) || cot <= 0) return toast.error("Cargá la cotización (ARS por USD)");
    }
    if (monedaPago === "USD" && (!Number.isFinite(cot) || cot <= 0)) {
      return toast.error("La cotización es obligatoria para pagos en dólares");
    }
    setSavingPago(true);
    try {
      await api.post("/pasivos/pagos", {
        deuda_id: pagoDeuda.id,
        monto,
        moneda_pago: monedaPago,
        cotizacion: Number.isFinite(cot) && cot > 0 ? cot : null,
        fecha: pagoForm.fecha || todayISO(),
        medio_pago_id: pagoForm.medio_pago_id,
        caja_id: medio.caja_id,
        observaciones: pagoForm.observaciones || null,
      });
      toast.success("Pago registrado");
      setPagoOpen(false);
      await loadDetalle(pagoDeuda._entidad.id);
      loadEntidades();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar pago");
    } finally {
      setSavingPago(false);
    }
  };

  const eliminarDeuda = async () => {
    if (!delDeuda) return;
    try {
      await api.delete(`/pasivos/deudas/${delDeuda.id}`);
      toast.success("Deuda eliminada");
      const eid = delDeuda.entidad_id;
      setDelDeuda(null);
      await loadDetalle(eid);
      loadEntidades();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const eliminarPago = async () => {
    if (!delPago) return;
    try {
      await api.delete(`/pasivos/pagos/${delPago.id}`);
      toast.success("Pago revertido");
      const eid = delPago.entidad_id;
      setDelPago(null);
      await loadDetalle(eid);
      loadEntidades();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revertir");
    }
  };

  const saldoGlobal = useMemo(
    () => entidades.reduce((s, e) => s + (Number(e.saldo_total) || 0), 0),
    [entidades]
  );
  const saldoGlobalUsd = useMemo(
    () => entidades.reduce((s, e) => s + (Number(e.saldo_total_usd) || 0), 0),
    [entidades]
  );

  return (
    <>
      <Topbar
        title="Pasivo Fin./Fiscal · Saldos de Entidades"
        subtitle="Deudas por entidad, con carga de pagos que descuentan caja / banco"
      />
      <div className="p-4 space-y-4">
        <div className="border border-[#E5E5E5] bg-white rounded-sm px-4 py-3 flex items-center justify-between">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div data-testid="saldo-global-ars">
              <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-mono">Saldo global pendiente · ARS</div>
              <div className="text-2xl font-heading font-bold text-[#A02836] mt-1">{fmtARS(saldoGlobal)}</div>
            </div>
            <div data-testid="saldo-global-usd">
              <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-mono">Saldo global pendiente · USD</div>
              <div className={`text-2xl font-heading font-bold mt-1 ${saldoGlobalUsd > 0 ? "text-emerald-700" : "text-neutral-400"}`}>
                {new Intl.NumberFormat("es-AR", { style: "currency", currency: "USD" }).format(saldoGlobalUsd)}
              </div>
            </div>
          </div>
          <Scales size={28} className="text-neutral-300 ml-4" />
        </div>

        <div className="space-y-2">
          {entidades.length === 0 ? (
            <div className="text-center text-neutral-400 py-10 border border-dashed border-neutral-200 rounded-sm">
              Sin entidades. Primero creá una en la pestaña "Entidades".
            </div>
          ) : entidades.map((e) => {
            const isOpen = !!expanded[e.id];
            const det = detalles[e.id];
            return (
              <div key={e.id} className="border border-[#E5E5E5] bg-white rounded-sm">
                <button
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50"
                  onClick={() => toggle(e.id)}
                  data-testid={`saldo-entidad-${e.id}`}
                >
                  {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  <span className="text-[11px] px-2 py-0.5 rounded-sm bg-neutral-100 uppercase tracking-wider font-mono">
                    {e.tipo || "Otro"}
                  </span>
                  <span className="font-semibold flex-1">{e.nombre}</span>
                  <span className="text-[12px] text-neutral-500 font-mono">{e.total_deudas || 0} deuda{(e.total_deudas || 0) !== 1 ? "s" : ""}</span>
                  <div className="w-[220px] text-right">
                    <div className="font-mono font-bold text-[#A02836]">{fmtARS(e.saldo_total)}</div>
                    {Number(e.saldo_total_usd) > 0 && (
                      <div className="font-mono text-[11px] text-emerald-700 font-semibold">
                        + {new Intl.NumberFormat("es-AR", { style: "currency", currency: "USD" }).format(e.saldo_total_usd)}
                      </div>
                    )}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-neutral-100 px-4 py-3 bg-neutral-50/60">
                    <div className="flex justify-end mb-2">
                      <Button
                        size="sm"
                        onClick={() => openNuevaDeuda(e)}
                        className="rounded-sm bg-[#A02836] hover:bg-[#821E2A] h-8"
                        data-testid={`nueva-deuda-${e.id}`}
                      >
                        <Plus size={12} className="mr-1" /> Nueva deuda
                      </Button>
                    </div>
                    {(!det || (det.deudas || []).length === 0) ? (
                      <div className="text-center text-[12px] text-neutral-400 py-4">Sin deudas para esta entidad.</div>
                    ) : (
                      <div className="space-y-3">
                        {det.deudas.map((d) => {
                          const pagos = (det.pagos || []).filter((p) => p.deuda_id === d.id);
                          const cancelada = Number(d.saldo) < 0.01;
                          return (
                            <div key={d.id} className={`border ${cancelada ? "border-emerald-200" : "border-neutral-200"} bg-white rounded-sm`}>
                              <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-100">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-[13px] truncate flex items-center gap-2">
                                    {d.descripcion}
                                    {(d.moneda || "ARS") === "USD" && (
                                      <span className="text-[9px] font-mono font-black uppercase bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded-sm">USD</span>
                                    )}
                                  </div>
                                  <div className="text-[11px] font-mono text-neutral-500 mt-0.5">
                                    Fecha: {d.fecha} · Total: {new Intl.NumberFormat("es-AR", { style: "currency", currency: d.moneda || "ARS" }).format(d.monto_total || 0)}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-[10px] uppercase font-mono tracking-widest text-neutral-500">Saldo</div>
                                  <div className={`font-mono font-bold ${cancelada ? "text-emerald-600" : "text-[#A02836]"}`}>
                                    {new Intl.NumberFormat("es-AR", { style: "currency", currency: d.moneda || "ARS" }).format(d.saldo || 0)}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  {!cancelada && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openNuevoPago(e, d)}
                                      className="h-8 rounded-sm"
                                      data-testid={`pagar-${d.id}`}
                                    >
                                      <CashRegister size={12} className="mr-1" /> Pagar
                                    </Button>
                                  )}
                                  <button
                                    onClick={() => setDelDeuda(d)}
                                    className="text-neutral-400 hover:text-red-600 p-1"
                                    title="Eliminar deuda"
                                    data-testid={`del-deuda-${d.id}`}
                                  >
                                    <TrashSimple size={14} />
                                  </button>
                                </div>
                              </div>
                              {pagos.length > 0 && (
                                <div className="px-3 py-2 text-[12px]">
                                  <div className="text-[10px] uppercase font-mono tracking-widest text-neutral-500 mb-1">Pagos</div>
                                  <table className="w-full">
                                    <tbody>
                                      {pagos.map((p) => {
                                        const monedaPago = (p.moneda_pago || "ARS").toUpperCase();
                                        const showEnMonedaPago = new Intl.NumberFormat("es-AR", { style: "currency", currency: monedaPago }).format(p.monto || 0);
                                        const mostrarArs = monedaPago === "USD" && p.monto_ars;
                                        return (
                                        <tr key={p.id} className="border-t border-neutral-50" data-testid={`pago-row-${p.id}`}>
                                          <td className="py-1 font-mono text-neutral-600 w-[100px]">{p.fecha}</td>
                                          <td className="py-1">{p.medio_pago_nombre || p.caja_nombre}</td>
                                          <td className="py-1 text-[11px] text-neutral-500">
                                            {p.observaciones || ""}
                                            {monedaPago === "USD" && p.cotizacion ? (
                                              <span className="ml-2 font-mono text-amber-700">@ {new Intl.NumberFormat("es-AR").format(p.cotizacion)}</span>
                                            ) : null}
                                          </td>
                                          <td className="py-1 text-right font-mono font-semibold text-emerald-700 w-[160px]">
                                            {showEnMonedaPago}
                                            {mostrarArs && (
                                              <div className="text-[10px] font-mono text-neutral-500 font-normal">
                                                ≈ {new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(p.monto_ars)} ARS
                                              </div>
                                            )}
                                          </td>
                                          <td className="py-1 text-right w-[30px]">
                                            <button
                                              onClick={() => setDelPago(p)}
                                              className="text-neutral-400 hover:text-red-600 p-1"
                                              title="Revertir pago"
                                              data-testid={`del-pago-${p.id}`}
                                            >
                                              <ReceiptX size={13} />
                                            </button>
                                          </td>
                                        </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Dialog nueva deuda */}
      <Dialog open={deudaOpen} onOpenChange={setDeudaOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Nueva deuda · {deudaEntidad?.nombre}</DialogTitle>
            <DialogDescription>Cargá el total adeudado; el saldo se descontará con cada pago.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[12px]">Descripción *</Label>
              <Input
                value={deudaForm.descripcion}
                onChange={(e) => setDeudaForm((f) => ({ ...f, descripcion: e.target.value }))}
                placeholder="Ej: Cuota 1/12"
                data-testid="deuda-form-desc"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[12px]">Monto total *</Label>
                <NumberInput
                  step="0.01"
                  value={deudaForm.monto_total}
                  onChange={(e) => setDeudaForm((f) => ({ ...f, monto_total: e.target.value }))}
                  data-testid="deuda-form-monto"
                />
              </div>
              <div>
                <Label className="text-[12px]">Moneda *</Label>
                <Select
                  value={deudaForm.moneda || "ARS"}
                  onValueChange={(v) => setDeudaForm((f) => ({ ...f, moneda: v }))}
                >
                  <SelectTrigger data-testid="deuda-form-moneda"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARS">ARS (Pesos)</SelectItem>
                    <SelectItem value="USD">USD (Dólares)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Fecha</Label>
                <Input
                  type="date"
                  value={deudaForm.fecha}
                  onChange={(e) => setDeudaForm((f) => ({ ...f, fecha: e.target.value }))}
                  data-testid="deuda-form-fecha"
                />
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Observaciones</Label>
              <Textarea
                value={deudaForm.observaciones}
                onChange={(e) => setDeudaForm((f) => ({ ...f, observaciones: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeudaOpen(false)}>Cancelar</Button>
            <Button onClick={guardarDeuda} disabled={savingDeuda} className="bg-[#A02836] hover:bg-[#821E2A]" data-testid="deuda-form-save">
              {savingDeuda ? "Guardando…" : "Guardar deuda"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog nuevo pago */}
      <Dialog open={pagoOpen} onOpenChange={setPagoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar pago</DialogTitle>
            <DialogDescription>
              Deuda: <b>{pagoDeuda?.descripcion}</b><br />
              Saldo actual: <b>{new Intl.NumberFormat("es-AR", { style: "currency", currency: (pagoDeuda?.moneda || "ARS") }).format(pagoDeuda?.saldo || 0)}</b>
              {(pagoDeuda?.moneda || "ARS") === "USD" && <span className="ml-2 text-[11px] font-mono uppercase text-amber-700">(deuda en USD)</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[12px]">Monto *</Label>
                <NumberInput
                  step="0.01"
                  value={pagoForm.monto}
                  onChange={(e) => setPagoForm((f) => ({ ...f, monto: e.target.value }))}
                  data-testid="pago-form-monto"
                />
              </div>
              <div>
                <Label className="text-[12px]">Moneda pago *</Label>
                <Select
                  value={pagoForm.moneda_pago || "ARS"}
                  onValueChange={(v) => setPagoForm((f) => ({ ...f, moneda_pago: v }))}
                >
                  <SelectTrigger data-testid="pago-form-moneda"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARS">ARS (Pesos)</SelectItem>
                    <SelectItem value="USD">USD (Dólares)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Fecha</Label>
                <Input
                  type="date"
                  value={pagoForm.fecha}
                  onChange={(e) => setPagoForm((f) => ({ ...f, fecha: e.target.value }))}
                />
              </div>
            </div>
            {/* Cotización: obligatoria si pago o deuda son en distintas monedas */}
            {(pagoForm.moneda_pago === "USD" || (pagoDeuda?.moneda || "ARS") === "USD") && (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 space-y-2">
                <div>
                  <Label className="text-[12px] font-semibold text-amber-900">
                    Cotización · ARS por 1 USD *
                  </Label>
                  <NumberInput
                    step="0.01"
                    placeholder="Ej: 1350"
                    value={pagoForm.cotizacion}
                    onChange={(e) => setPagoForm((f) => ({ ...f, cotizacion: e.target.value }))}
                    data-testid="pago-form-cotizacion"
                  />
                </div>
                {/* Preview de conversión */}
                {(() => {
                  const monto = parseFloat(pagoForm.monto);
                  const cot = parseFloat(pagoForm.cotizacion);
                  if (!Number.isFinite(monto) || monto <= 0 || !Number.isFinite(cot) || cot <= 0) return null;
                  const monedaPago = pagoForm.moneda_pago || "ARS";
                  const monedaDeuda = pagoDeuda?.moneda || "ARS";
                  const salidaArs = monedaPago === "USD" ? monto * cot : monto;
                  let descuentoDeuda;
                  if (monedaPago === monedaDeuda) descuentoDeuda = monto;
                  else if (monedaPago === "USD" && monedaDeuda === "ARS") descuentoDeuda = monto * cot;
                  else descuentoDeuda = monto / cot;
                  return (
                    <div className="text-[11px] font-mono text-amber-900 space-y-0.5">
                      <div>Sale de la caja (ARS): <b>{new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(salidaArs)}</b></div>
                      <div>Descuenta de la deuda ({monedaDeuda}): <b>{new Intl.NumberFormat("es-AR", { style: "currency", currency: monedaDeuda }).format(descuentoDeuda)}</b></div>
                    </div>
                  );
                })()}
              </div>
            )}
            <div>
              <Label className="text-[12px]">Medio de pago *</Label>
              <Select
                value={pagoForm.medio_pago_id}
                onValueChange={(v) => setPagoForm((f) => ({ ...f, medio_pago_id: v }))}
              >
                <SelectTrigger data-testid="pago-form-medio"><SelectValue placeholder="Elegí un medio de pago" /></SelectTrigger>
                <SelectContent>
                  {mediosPago.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-neutral-500">
                      No hay medios de pago configurados. Cargalos en Admin → Medios de Pago.
                    </div>
                  )}
                  {mediosPago.map((m) => {
                    const cajaNombre = cajas.find((c) => c.id === m.caja_id)?.nombre || "";
                    return (
                      <SelectItem key={m.id} value={m.id}>
                        {m.nombre}{cajaNombre ? ` · ${cajaNombre}` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-neutral-500 mt-1">
                La caja imputable se resuelve automáticamente desde el medio de pago (igual que en Ventas / Compras / POS).
              </p>
            </div>
            <div>
              <Label className="text-[12px]">Observaciones</Label>
              <Textarea
                value={pagoForm.observaciones}
                onChange={(e) => setPagoForm((f) => ({ ...f, observaciones: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagoOpen(false)}>Cancelar</Button>
            <Button onClick={guardarPago} disabled={savingPago} className="bg-[#A02836] hover:bg-[#821E2A]" data-testid="pago-form-save">
              {savingPago ? "Guardando…" : "Registrar pago"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delDeuda} onOpenChange={(v) => !v && setDelDeuda(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar deuda</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar "{delDeuda?.descripcion}"? Sólo se permite si no tiene pagos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarDeuda} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delPago} onOpenChange={(v) => !v && setDelPago(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Revertir pago</AlertDialogTitle>
            <AlertDialogDescription>
              Se sumará {fmtARS(delPago?.monto || 0)} al saldo de la deuda y a la caja "{delPago?.caja_nombre}". ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarPago} className="bg-red-600 hover:bg-red-700">Revertir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
